import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { ConsultCTA } from "../components/ConsultCTA";
import { Icon } from "../components/Icon";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHeading } from "../components/Reveal";
import { Timeline } from "../components/Timeline";
import { IMAGES } from "../data/site";
import { usePageMeta } from "../hooks/usePageMeta";

const SIGNS = [
    "Is not saying first words around the expected age",
    "Uses very few words compared to other children their age",
    "Is not combining words into short phrases or sentences",
    "Is difficult for family members or others to understand",
    "Relies more on gestures, pointing, or behaviours than words",
    "Seems to understand less than expected for their age",
    "Becomes frustrated when trying to communicate",
    "Has lost words or skills they previously used",
];

const SUPPORT_AREAS = [
    {
        icon: "MessagesSquare",
        title: "Speech & Language Development",
        description:
            "A thorough understanding of where your child's speech and language skills are right now — and the next steps that suit them.",
    },
    {
        icon: "BookOpen",
        title: "Vocabulary Development",
        description:
            "Building useful, meaningful words through play, routines, and activities your child genuinely enjoys.",
    },
    {
        icon: "PenLine",
        title: "Sentence Formation",
        description:
            "Supporting the move from single words to phrases and sentences used in everyday situations.",
    },
    {
        icon: "MessageCircle",
        title: "Functional Communication",
        description:
            "Helping your child express needs, wants, choices, and feelings in ways that work in daily life.",
    },
    {
        icon: "Heart",
        title: "Parent Involvement",
        description:
            "You receive practical guidance and home activities so progress continues naturally between sessions.",
    },
    {
        icon: "TrendingUp",
        title: "Therapy Process",
        description:
            "Assessment, individualized goals, structured sessions, home practice, and regular progress reviews.",
    },
];

const SpeechDelay = () => {
    usePageMeta(
        "Speech Delay Therapy in Patna | Speech Horizon — Anishabad",
        "Concerned about your child's speech or language development? Speech delay assessment and individualized therapy in Anishabad, Patna at Speech Horizon. Call 9973416270."
    );

    return (
        <div data-testid="speech-delay-page">
            <PageHero
                eyebrow="Speech Delay"
                lines={["Understanding"]}
                highlight="Speech Delay"
                description="Every child develops at their own pace. When speech and language milestones are significantly delayed, professional guidance can help identify the right support."
            />

            <section className="bg-cream py-20 md:py-28" data-testid="speech-delay-signs-section">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-8 lg:grid-cols-2">
                    <Reveal className="relative order-2 lg:order-1">
                        <div className="absolute -left-5 -top-5 h-full w-full rotate-[-2deg] rounded-[2.5rem] bg-peach-soft" />
                        <img
                            src={IMAGES.reading}
                            alt="Children sharing a book, building early language skills"
                            className="relative h-[420px] w-full rounded-[2.5rem] object-cover shadow-xl shadow-navy/10 md:h-[540px]"
                        />
                    </Reveal>
                    <div className="order-1 lg:order-2">
                        <SectionHeading
                            align="left"
                            eyebrow="The Signs"
                            title="What Speech Delay Can Look Like"
                            description="Speech delay can look different from child to child. You may notice that your child:"
                        />
                        <Reveal delay={0.15}>
                            <ul className="mt-8 space-y-3.5" data-testid="speech-delay-signs-list">
                                {SIGNS.map((sign) => (
                                    <li key={sign} className="flex items-start gap-3 text-[15px] leading-relaxed text-ink/75">
                                        <CheckCircle2 size={19} className="mt-0.5 shrink-0 text-teal" />
                                        {sign}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="speech-delay-assessment-section">
                <div className="mx-auto max-w-4xl px-6 text-center md:px-8">
                    <SectionHeading
                        eyebrow="When to Reach Out"
                        title="When Parents May Consider an Assessment"
                        description="If you find yourself comparing your child's speech with other children, or feeling unsure whether their communication is developing as expected, it can help to seek a professional opinion. An assessment is simply a way to understand your child's communication — not a label."
                    />
                    <Reveal delay={0.2}>
                        <div className="mt-10 rounded-[2rem] border-l-4 border-teal bg-white p-8 text-left shadow-xl shadow-teal/5 md:text-center">
                            <p className="font-jakarta text-lg font-extrabold leading-snug text-navy md:text-2xl">
                                If you have concerns about your child&rsquo;s speech or language development, a
                                professional assessment can help{" "}
                                <span className="text-teal">identify their individual communication needs.</span>
                            </p>
                            <Link
                                to="/contact"
                                data-testid="speech-delay-book-btn"
                                className="group mt-7 inline-flex items-center gap-2 rounded-full bg-peach px-8 py-4 font-jakarta text-sm font-bold text-navy shadow-xl shadow-peach/30 transition-all duration-300 hover:-translate-y-1 hover:bg-peach-deep hover:text-cream"
                            >
                                Book a Consultation
                                <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-cream py-20 md:py-28" data-testid="speech-delay-support-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="How We Help"
                        title="Areas We Support"
                        description="Therapy is individualized and goal-oriented — built around your child's specific needs, strengths, and pace."
                    />
                    <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {SUPPORT_AREAS.map((area, i) => (
                            <Reveal key={area.title} delay={i * 0.06}>
                                <div className="group h-full rounded-[1.75rem] border border-navy/5 bg-white p-7 transition-all duration-500 hover:-translate-y-2 hover:border-teal/25 hover:shadow-xl hover:shadow-teal/10">
                                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-teal-soft text-teal-dark transition-transform duration-500 group-hover:scale-110">
                                        <Icon name={area.icon} size={21} strokeWidth={1.9} />
                                    </span>
                                    <h3 className="mt-5 font-jakarta text-lg font-bold text-navy">{area.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-ink/65">{area.description}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-mist py-20 md:py-28" data-testid="speech-delay-process-section">
                <div className="mx-auto max-w-7xl px-6 md:px-8">
                    <SectionHeading
                        eyebrow="The Process"
                        title="How Therapy Works"
                        description="A clear, structured journey — from first assessment to confident everyday communication."
                    />
                    <Timeline />
                </div>
            </section>

            <ConsultCTA
                title="Book a Consultation"
                text="If you have concerns about your child's speech or language development, take the first step today. A professional assessment can help identify their individual communication needs and the right therapy support."
            />
        </div>
    );
};

export default SpeechDelay;
